/**
 * AdminGate — top-level wrapper around arbi-dev's AppShell. ADR 37.
 *
 * State machine:
 *   loading           → LoadingScreen
 *   !user             → cross-subdomain redirect to arbi.ironcladsupply.co/login
 *   user && !role     → LoadingScreen (profile fetch in flight)
 *   role !== 'admin'  → cross-subdomain redirect to arbi.ironcladsupply.co/dashboard
 *   role === 'admin'  → render children
 *
 * `window.location.replace` is correct over React Router's <Navigate> because
 * these are cross-origin hard navigations — Router can only navigate within
 * the same origin. In dev mode (vite dev server, vercel preview URLs), the
 * redirect is logged-and-skipped so local development doesn't bounce to
 * prod arbi.
 */
import { type JSX, type ReactNode } from 'react';
import { useApp } from '../hooks/useApp';
import LoadingScreen from './LoadingScreen';

const ARBI_LOGIN_URL = 'https://arbi.ironcladsupply.co/login?next=arbi-dev';
const ARBI_DASHBOARD_URL = 'https://arbi.ironcladsupply.co/dashboard';

function redirectIfPossible(target: string): void {
  if (typeof window === 'undefined') return;
  if (import.meta.env.PROD) {
    window.location.replace(target);
  } else {
    console.warn(
      '[arbi-dev/AdminGate] Would redirect to ' + target + ' (dev mode — skipping)'
    );
  }
}

interface AdminGateProps {
  children: ReactNode;
}

export default function AdminGate({ children }: AdminGateProps): JSX.Element {
  const { user, role, loading } = useApp();

  if (loading) return <LoadingScreen />;

  if (!user) {
    redirectIfPossible(ARBI_LOGIN_URL);
    return <LoadingScreen message="Redirecting to sign in…" />;
  }

  if (!role) return <LoadingScreen />;

  if (role !== 'admin') {
    redirectIfPossible(ARBI_DASHBOARD_URL);
    return <LoadingScreen message="Admin access required. Redirecting…" />;
  }

  return <>{children}</>;
}
